import { useEffect, useState } from "react";
import { startAuthentication } from "@simplewebauthn/browser";
import { Fingerprint, KeyRound, ShieldCheck, LogOut, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { passkeySupported } from "../lib/passkey";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const err = (e, d) => e?.response?.data?.detail || d;

export default function StepUpGate() {
  const { logout } = useAuth();
  const [st, setSt] = useState(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState("");
  const finish = () => { toast.success("تم التحقق"); window.location.replace("/"); };

  useEffect(() => { api.stepUpStatus(window.location.origin).then(setSt).catch(() => window.location.replace("/")); }, []);

  const bio = async () => {
    setBusy("bio");
    try {
      const origin = window.location.origin;
      const o = await api.stepUpPkOptions(origin);
      const credential = await startAuthentication({ optionsJSON: o.options });
      await api.stepUpPkVerify({ challenge_id: o.challenge_id, credential, origin });
      finish();
    } catch (e) { toast.error(err(e, "تعذّر التحقق بالبصمة، استخدم كلمة المرور")); } finally { setBusy(""); }
  };
  const password = async (e) => {
    e.preventDefault();
    if (!st.has_password && pw !== pw2) return toast.error("كلمتا المرور غير متطابقتين");
    setBusy("pw");
    try { await (st.has_password ? api.stepUpPassword(pw) : api.stepUpSetPassword(pw)); finish(); }
    catch (e2) { toast.error(err(e2, "تعذّر التحقق")); } finally { setBusy(""); }
  };

  if (!st) return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const canBio = st.has_passkey && passkeySupported();
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4 font-body" dir="rtl" data-testid="step-up-gate">
      <div className="w-full max-w-sm rounded-3xl border border-slate-200 bg-white p-6 shadow-xl">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50"><ShieldCheck className="h-7 w-7 text-emerald-600" /></div>
        <h1 className="text-center font-heading text-xl font-bold text-slate-900">تأكيد الدخول</h1>
        <p className="mt-1 text-center text-sm text-slate-500">دخلت بـ Google ({st.email}). أكّد هويتك للمتابعة.</p>
        {canBio && (
          <Button onClick={bio} disabled={!!busy} className="mt-6 h-12 w-full gap-2 rounded-xl bg-emerald-600 text-base hover:bg-emerald-700" data-testid="step-up-bio">
            {busy === "bio" ? <Loader2 className="h-5 w-5 animate-spin" /> : <Fingerprint className="h-5 w-5" />} تأكيد بالبصمة
          </Button>
        )}
        {canBio && <div className="my-4 flex items-center gap-2 text-xs text-slate-400"><span className="h-px flex-1 bg-slate-200" />أو<span className="h-px flex-1 bg-slate-200" /></div>}
        <form onSubmit={password} className={`space-y-3 ${canBio ? "" : "mt-6"}`}>
          {!st.has_password && <p className="rounded-xl bg-amber-50 p-3 text-xs text-amber-800" data-testid="step-up-set-hint">ما عندك كلمة مرور للموقع. أنشئ كلمة مرور الحين، وبعدها تقدر تفعّل البصمة من الإعدادات.</p>}
          <div className="space-y-1.5">
            <Label htmlFor="su-pw">{st.has_password ? "كلمة مرور حسابك في الموقع" : "رمز سري جديد (6 أرقام)"}</Label>
            <Input id="su-pw" type="password" dir="ltr" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete={st.has_password ? "current-password" : "new-password"} data-testid="step-up-password" />
          </div>
          {!st.has_password && (
            <div className="space-y-1.5">
              <Label htmlFor="su-pw2">تأكيد كلمة المرور</Label>
              <Input id="su-pw2" pin value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="step-up-password2" />
            </div>
          )}
          <Button type="submit" variant={canBio ? "outline" : "default"} disabled={!!busy || !pw} className="h-11 w-full gap-2 rounded-xl" data-testid="step-up-submit">
            {busy === "pw" ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} {st.has_password ? "تأكيد بكلمة المرور" : "حفظ كلمة المرور والمتابعة"}
          </Button>
        </form>
        <button onClick={logout} className="mx-auto mt-5 flex items-center gap-1 text-xs text-slate-400 hover:text-rose-600" data-testid="step-up-logout"><LogOut className="h-3.5 w-3.5" /> خروج</button>
      </div>
    </div>
  );
}
